import {CurrentUser} from 'app/core/services/auth.service';
import {AppUrls, path} from 'app/core/url/app-urls.service';
import {AppPlatformIcons} from 'app/features/apps/interfaces/app-list.interface';
import {NavItem} from 'app/components/navigation-side-bar/navigation-side-bar.component';
import {AppInfo} from 'app/core/services/current-app/selected-app.service';

const manageConfigurationPageInDcc = (currentAppInfo: AppInfo) => {
    return Boolean(currentAppInfo.id) && !currentAppInfo.permissions?.canViewNewMediationSettings;
};

export function allItems(
    currentUser: CurrentUser,
    currentAppInfo: AppInfo,
    faqURL: string,
    navExpanded: boolean,
    language: string
): NavItem[] {
    if (!currentUser) {
        return [];
    }
    return [
        {
            id: 'analytics',
            useRouterLink: true,
            link: AppUrls.ANALYTICS,
            iconClass: 'icn icn-dashboard',
            label: 'navigation.analytics',
            visible: true,
            children: [
                {
                    id: 'dashboard.tabs.overview',
                    link: AppUrls.BUSINESS_OVERVIEW,
                    iconClass: 'icn icn-dashboard',
                    label: 'dashboard.tabs.overview',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: currentUser.permissions.canViewPublishersDashboard,
                },
                {
                    id: 'dashboard.tabs.monetization',
                    link: AppUrls.MONETIZATION,
                    iconClass: 'icn icn-dashboard',
                    label: 'dashboard.tabs.monetization',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: currentUser.permissions.canViewPublishersDashboard,
                },
                {
                    id: 'dashboard.tabs.reports',
                    link: AppUrls.ANALYTICS_REPORTS,
                    iconClass: 'icn icn-dashboard',
                    label: 'dashboard.tabs.reports',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: currentUser.permissions.canViewPublishersDashboard || !currentUser.permissions.canViewOldDashboard,
                },
                {
                    id: 'dashboard.tabs.dashboard',
                    link: ['v3', AppUrls.DASHBOARD].join('/'),
                    iconClass: 'icn icn-dashboard',
                    label: 'dashboard.tabs.dashboard',
                    useRouterLink: true,
                    exactMatch: true,
                    visible: currentUser.permissions.canViewOldDashboard,
                },
                {
                    id: 'dashboard.tabs.internalDashboard',
                    link: AppUrls.DASHBOARD_INTERNAL,
                    iconClass: 'icn',
                    label: 'dashboard.tabs.internalDashboard',
                    useRouterLink: true,
                    visible: currentUser.permissions.canViewInternalDashboard,
                },
                {
                    id: 'dashboard.tabs.retentionRate',
                    link: AppUrls.DASHBOARD_RETENTION_RATE,
                    iconClass: 'icn',
                    label: 'dashboard.tabs.retentionRate',
                    visible: currentUser.permissions.canViewDashboardRetentionRate,
                },
                {
                    id: 'dashboard.tabs.ltv',
                    link: AppUrls.DASHBOARD_LTV,
                    iconClass: 'icn',
                    label: 'dashboard.tabs.ltv',
                    visible: currentUser.permissions.canViewDashboardLtv,
                },
            ],
        },
        {
            id: 'whitelabel/users',
            link: path(AppUrls.WHITE_LABEL_USERS, currentUser.wlDomainId),
            iconClass: 'icn icn-user',
            label: 'navigation.users',
            visible: currentUser.permissions.canViewWhiteLabel,
        },
        {
            id: 'whitelabel',
            link: AppUrls.WHITE_LABEL,
            iconClass: 'icn icn-api-integration',
            label: 'navigation.whitelabel',
            visible: currentUser.permissions.canViewWhiteLabel,
        },
        {
            id: 'apps',
            link: AppUrls.APPS,
            useRouterLink: false,
            iconClass: 'icn icn-apps',
            label: shortLabelMenu(currentUser, 'navigation.apps', navExpanded),
            visible: currentUser.permissions.canViewApps,
            children: [
                {
                    id: 'apps.appList',
                    link: AppUrls.APPS,
                    exactMatch: true,
                    useRouterLink: true,
                    iconClass: 'icn icn-apps',
                    label: shortLabelMenu(currentUser, Boolean(currentAppInfo.id) ? 'navigation.appList' : 'navigation.apps', navExpanded),
                    visible: currentUser.permissions.canViewApps,
                },
                {
                    id: 'apps.appEdit',
                    link: AppUrls.APPS_UPDATE,
                    useRouterLink: true,
                    iconClass: AppPlatformIcons[`${currentAppInfo.platform}`],
                    label: currentAppInfo.name,
                    visible: Boolean(currentAppInfo.id),
                },
            ],
        },
        {
            id: 'mediationSetup',
            link: AppUrls.INTEGRATIONS_MEDIATION_AD_NETWORKS,
            iconClass: 'icn icn-mediation',
            label: 'navigation.mediation',
            visible: true,
            children: [
                {
                    id: 'integrations/mediationAdNetworks',
                    link: AppUrls.INTEGRATIONS_MEDIATION_AD_NETWORKS,
                    iconClass: 'icn',
                    label: 'navigation.adNetworks',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: true,
                },
                {
                    id: 'apps/dcc/mediationGroups',
                    link: AppUrls.DCC_MEDIATION_GROUPS,
                    iconClass: 'icn',
                    label: 'navigation.mediationGroups',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: currentAppInfo.permissions?.canViewNewMediationSettings,
                    sup: 'dcc.mediationGroups.newSup',
                },
                {
                    id: 'integrations/mediations',
                    link: AppUrls.MEDIATIONS,
                    iconClass: 'icn',
                    label: 'navigation.mediations',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: currentUser.permissions.canViewNewMediationSettings,
                },
                {
                    id: 'apps/dcc/lineItems',
                    link: AppUrls.DCC_LINE_ITEMS,
                    iconClass: 'icn',
                    label: 'navigation.lineItems',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: Boolean(currentAppInfo.id),
                },
                {
                    id: 'v2/placement',
                    link: ['v3', AppUrls.APP_PLACEMENT].join('/'),
                    iconClass: 'icn',
                    label: 'navigation.placements',
                    useRouterLink: true,
                    reloadRoute: true,
                    exactMatch: false,
                    visible: Boolean(currentAppInfo.id),
                },
                {
                    id: 'apps/dcc/configurations',
                    link: AppUrls.DCC_CONFIGURATIONS,
                    iconClass: 'icn',
                    label: 'navigation.adWaterfall',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: manageConfigurationPageInDcc(currentAppInfo),
                },
                {
                    id: 'v2/segments',
                    link: ['v3', AppUrls.SEGMENTS].join('/'),
                    iconClass: 'icn icn-segments',
                    label: 'navigation.segments',
                    useRouterLink: true,
                    reloadRoute: true,
                    visible: currentUser.permissions.canViewSegments,
                },
                {
                    id: 'header.userMenu.appAdsTxt',
                    link: AppUrls.USER_PROFILE_ADS_TXT,
                    iconClass: 'icn',
                    label: 'navigation.appTxt',
                    visible: true,
                    useRouterLink: true,
                },
            ],
        },
        {
            id: 'abTesting',
            link: AppUrls.EXPERIMENTS_COLLECTION,
            iconClass: 'icn icn-experiments',
            label: 'navigation.abTesting',
            useRouterLink: true,
            visible: currentUser.permissions.canViewExperiments,
            children: [
                {
                    id: 'experiments/collection',
                    link: AppUrls.EXPERIMENTS_COLLECTION,
                    iconClass: 'icn icn-experiments',
                    label: 'navigation.experiments.list',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: true,
                },
                {
                    id: 'experiments/results',
                    link: '',
                    iconClass: 'icn icn-experiments',
                    label: 'navigation.experiments.results',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: false,
                },
            ],
        },

        {
            id: 'integrations/userAcquisition',
            link: AppUrls.INTEGRATIONS_USER_ACQUISITION,
            iconClass: 'icn icn-user-acquisition',
            label: shortLabelMenu(currentUser, 'navigation.userAcquisition', navExpanded),
            useRouterLink: true,
            exactMatch: false,
            visible: true,
            children: [
                {
                    id: 'creativeLibraryLink',
                    link: AppUrls.CREATIVE_LIBRARY,
                    iconClass: 'icn icn-creativeLibrary',
                    label: 'navigation.creativeLibrary',
                    visible: document.location.pathname.startsWith('/' + AppUrls.CREATIVE_LIBRARY),
                },
                {
                    id: 'campaignManagement',
                    link: '/analytics/reports',
                    iconClass: 'icn',
                    label: 'navigation.campaignManagement',
                    useRouterLink: true,
                    visible: currentUser.permissions.canViewPublishersDashboard || !currentUser.permissions.canViewOldDashboard,
                },
                {
                    id: 'userAcquisition/setup',
                    link: AppUrls.INTEGRATIONS_USER_ACQUISITION,
                    useRouterLink: true,
                    iconClass: 'icn',
                    label: 'navigation.setup',
                    visible: true,
                    exactMatch: false,
                    children: [
                        {
                            id: 'userAcquisition/setup/mmp_accounts',
                            link: AppUrls.INTEGRATIONS_USER_ACQUISITION,
                            iconClass: 'icn',
                            label: 'navigation.mmp',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: true,
                        },
                        {
                            id: 'userAcquisition/setup/network_accounts',
                            link: AppUrls.INTEGRATIONS_BID_MANAGER,
                            iconClass: 'icn',
                            label: 'navigation.network',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: currentUser.permissions.bidManagerPermissions.canManageUaAccounts,
                        },
                        {
                            id: 'userAcquisition/setup/attribution_settings',
                            link: AppUrls.APP_ATTRIBUTION_SETTINGS,
                            iconClass: 'icn',
                            label: 'navigation.attribution',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: currentUser.permissions.canViewApps && Boolean(currentAppInfo.id),
                        },

                        // creative Library links
                        {
                            id: 'creativeLibrary/uploads',
                            link: AppUrls.CREATIVE_LIBRARY_UPLOADS,
                            useRouterLink: true,
                            iconClass: 'icn icn-creativeLibrary',
                            label: 'creativeLibrary.uploads',
                            exactMatch: false,
                            queryParams: 'preserve',
                            visible: document.location.pathname.startsWith('/' + AppUrls.CREATIVE_LIBRARY),
                        },
                        {
                            id: 'creativeLibrary/creatives',
                            link: AppUrls.CREATIVE_LIBRARY_CREATIVES,
                            useRouterLink: true,
                            iconClass: 'icn icn-creativeLibrary',
                            label: 'creativeLibrary.creatives',
                            exactMatch: false,
                            queryParams: 'preserve',
                            visible: document.location.pathname.startsWith('/' + AppUrls.CREATIVE_LIBRARY),
                        },
                        {
                            id: 'creativeLibrary/testResults',
                            link: AppUrls.CREATIVE_LIBRARY_TEST_RESULTS,
                            useRouterLink: true,
                            iconClass: 'icn icn-creativeLibrary',
                            label: 'creativeLibrary.testResults',
                            exactMatch: false,
                            queryParams: 'preserve',
                            visible: document.location.pathname.startsWith('/' + AppUrls.CREATIVE_LIBRARY),
                        },
                        {
                            id: 'creativeLibrary/testFlow',
                            link: AppUrls.CREATIVE_LIBRARY_TEST_FLOW,
                            useRouterLink: true,
                            iconClass: 'icn icn-creativeLibrary',
                            label: 'creativeLibrary.testFlow',
                            exactMatch: false,
                            queryParams: 'preserve',
                            visible: document.location.pathname.startsWith('/' + AppUrls.CREATIVE_LIBRARY),
                        },
                    ],
                },
            ],
        },
        {
            id: 'bidon',
            link: AppUrls.BIDON,
            iconClass: 'icn icn-bidon',
            label: 'navigation.bidon',
            secure: true,
            visible: currentUser.permissions.canViewEmbeddedBidonUi,
        },
        {
            id: 'bidManager',
            link: AppUrls.BID_MANAGER,
            iconClass: 'icn icn-bidmanager',
            label: 'bidManager.pageTitle',
            visible:
                currentUser.permissions.bidManagerPermissions.canViewBidmanager &&
                (currentUser.permissions.bidManagerPermissions.canManageCreatives ||
                    currentUser.permissions.bidManagerPermissions.canManageCampaigns),
            children: [
                {
                    id: 'bidManager.creatives',
                    link: AppUrls.BID_MANAGER_CREATIVES,
                    useRouterLink: true,
                    iconClass: 'icn icn-bidmanager',
                    label: 'bidManager.creativesPage',
                    visible: currentUser.permissions.bidManagerPermissions.canManageCreatives,
                    exactMatch: false,
                },
                {
                    id: 'bidManager.campaigns',
                    link: AppUrls.BID_MANAGER_CAMPAIGNS,
                    useRouterLink: true,
                    iconClass: 'icn icn-bidmanager',
                    label: 'bidManager.tabs.campaigns',
                    visible: currentUser.permissions.bidManagerPermissions.canManageCampaigns,
                    exactMatch: false,
                },
                {
                    id: 'creativeLibrary',
                    link: AppUrls.CREATIVE_LIBRARY_CREATIVES,
                    useRouterLink: true,
                    iconClass: 'icn icn-bidmanager',
                    label: 'bidManager.tabs.creativeLibrary',
                    visible: currentUser.permissions.bidManagerPermissions.canManageCampaigns,
                    exactMatch: false,
                },
                {
                    id: 'creativeLibrary.uploads',
                    link: AppUrls.CREATIVE_LIBRARY_UPLOADS,
                    useRouterLink: true,
                    iconClass: 'icn icn-bidmanager',
                    label: 'bidManager.tabs.upload',
                    visible: currentUser.permissions.bidManagerPermissions.canManageCampaigns,
                    exactMatch: false,
                },
                {
                    id: 'bidManager.accounts',
                    link: AppUrls.INTEGRATIONS_BID_MANAGER,
                    useRouterLink: true,
                    iconClass: 'icn icn-bidmanager',
                    label: 'bidManager.tabs.accounts',
                    visible: currentUser.permissions.bidManagerPermissions.canManageUaAccounts,
                    exactMatch: false,
                },
            ],
        },

        {
            id: 'funnels',
            link: AppUrls.ANALYTICS_FUNNELS,
            iconClass: 'icn icn-funnels',
            label: 'navigation.funnels.collection',
            useRouterLink: true,
            visible: currentUser.permissions.canViewFunnels,
            sup: 'beta',
            children: [
                {
                    id: 'funnels/collection',
                    link: AppUrls.ANALYTICS_FUNNELS,
                    iconClass: 'icn icn-funnels',
                    label: 'navigation.funnels.collection',
                    useRouterLink: true,
                    visible: currentUser.permissions.canViewFunnels,
                },
                {
                    id: 'funnels/events',
                    link: AppUrls.APP_EVENTS,
                    iconClass: 'icn icn-funnels',
                    label: 'navigation.funnels.events',
                    useRouterLink: true,
                    visible: currentUser.permissions.canViewFunnels,
                },
            ],
        },
        {
            id: 'adServer',
            link: ['v3', AppUrls.AD_SERVER].join('/'),
            iconClass: 'icn icn-campaigns',
            label: shortLabelMenu(currentUser, 'navigation.ad_server', navExpanded),
            useRouterLink: true,
            reloadRoute: true,
            visible: currentUser.permissions.canViewAdServer,
        },
        {
            id: 'billing',
            link: AppUrls.PAYOUTS_REPORT,
            useRouterLink: true,
            iconClass: 'icn icn-billing',
            label: 'navigation.billing',
            visible: currentUser.permissions.canViewPayouts,
            children: [
                {
                    id: 'payouts.tabs.reports',
                    link: AppUrls.PAYOUTS_REPORT,
                    useRouterLink: true,
                    iconClass: 'icn icn-billing',
                    label: 'payouts.tabs.reports',
                    visible: true,
                },
                {
                    id: 'payouts.tabs.requests',
                    link: AppUrls.PAYOUTS_REQUEST,
                    useRouterLink: true,
                    iconClass: 'icn icn-billing',
                    label: 'payouts.tabs.requests',
                    visible: true,
                },
                {
                    id: 'payouts.tabs.details',
                    link: AppUrls.PAYOUTS_DETAILS,
                    useRouterLink: true,
                    iconClass: 'icn icn-billing',
                    label: 'payouts.tabs.details',
                    visible: true,
                },
                {
                    id: 'payouts.tabs.publishing',
                    link: AppUrls.PAYOUTS_PUBLISHING,
                    useRouterLink: true,
                    iconClass: 'icn icn-billing',
                    label: 'payouts.tabs.publishing',
                    visible: document.location.pathname.startsWith('/' + AppUrls.PAYOUTS_PUBLISHING),
                },
                {
                    id: 'payouts.tabs.ambassadorProgram',
                    link: ['v3', AppUrls.PAYOUTS_AMBASSADOR_PROGRAM].join('/'),
                    useRouterLink: true,
                    iconClass: 'icn icn-billing',
                    label: 'payouts.tabs.ambassadorProgram',
                    visible: currentUser.permissions.canViewAmbassadorProgram,
                },
            ],
        },
        {
            id: 'documentation',
            link: path(AppUrls.DOCS, language),
            target: '_blank',
            iconClass: 'icn icn-docs',
            label: shortLabelMenu(currentUser, 'navigation.docs', navExpanded),
            visible: currentUser.permissions.canViewSdkDocs,
            trackEvent: 'Docs clicked',
        },
        {
            id: 'support',
            link: faqURL || AppUrls.FAQ,
            iconClass: 'icn icn-support',
            label: shortLabelMenu(currentUser, 'navigation.support', navExpanded),
            visible: true,
            children: [
                {
                    id: 'contact_us',
                    link: 'https://mail.google.com/mail/?view=cm&fs=1&to=support@appodeal.com',
                    target: '_blank',
                    iconClass: 'icn icn-contact-us',
                    label: 'navigation.contactUs',
                    visible: true,
                    openIntercom: false,
                    trackEvent: 'Contact us clicked',
                },
                {
                    id: 'faq',
                    // url can be custom
                    link: faqURL || AppUrls.FAQ,
                    iconClass: 'icn icn-faq',
                    label: 'navigation.faq',
                    target: '_blank',
                    visible: currentUser.permissions.canViewFaq,
                    trackEvent: 'FAQ clicked',
                },
                {
                    id: 'integration',
                    link: path(AppUrls.API_INTEGRATION, language, language === 'ru' ? 'api-dokumentatsiya' : 'reporting-api'),
                    target: '_blank',
                    iconClass: 'icn icn-api-integration',
                    label: 'navigation.api',
                    visible: currentUser.permissions.canViewIntegrations,
                    trackEvent: 'API documentation clicked',
                },
                {
                    id: 'homepage',
                    link: path(AppUrls.APPODEAL_HOMEPAGE, language),
                    target: '_blank',
                    iconClass: 'icn',
                    label: 'navigation.appodealHomepage',
                    visible: true,
                },
                {
                    id: 'appodealBlog',
                    link: path(AppUrls.APPODEAL_BLOG, language),
                    target: '_blank',
                    iconClass: 'icn',
                    label: 'navigation.appodealBlog',
                    visible: true,
                },
            ],
        },
        {
            id: 'myAccounts',
            link: AppUrls.USER_PROFILE,
            iconClass: 'icn icn-my-account',
            label: 'navigation.account',
            useRouterLink: true,
            visible: true,
            children: [
                {
                    id: 'settings',
                    link: AppUrls.USER_PROFILE,
                    iconClass: 'icn',
                    label: 'navigation.settings.main',
                    visible: true,
                    useRouterLink: true,
                    children: [
                        {
                            id: 'account',
                            link: AppUrls.USER_PROFILE_EDIT_ACCOUNT,
                            iconClass: 'icn',
                            label: 'navigation.settings.account',
                            visible: true,
                            useRouterLink: true,
                        },
                        {
                            id: 'security',
                            link: AppUrls.USER_PROFILE_EDIT_SECURITY,
                            iconClass: 'icn',
                            label: 'navigation.settings.security',
                            visible: true,
                            useRouterLink: true,
                        },
                        {
                            id: 'apiCredentials',
                            link: AppUrls.USER_PROFILE_CREDENTIALS,
                            iconClass: 'icn',
                            label: 'navigation.settings.api',
                            visible: true,
                            useRouterLink: true,
                        },
                    ],
                },
                {
                    id: 'accessSharing',
                    link: AppUrls.APPS_SHARING,
                    iconClass: 'icn',
                    label: 'navigation.accessSharing',
                    visible: true,
                    useRouterLink: true,
                    reloadRoute: true,
                },
            ],
        },

        {
            id: 'admin',
            link: AppUrls.PANEL,
            iconClass: 'icn icn-panel',
            label: 'navigation.panel',
            secure: true,
            visible: currentUser.permissions.canViewAdminPanel,
        },
    ];
}

export function allItemsExperimental(
    currentUser: CurrentUser,
    currentAppInfo: AppInfo,
    faqURL: string,
    navExpanded: boolean,
    language: string
): NavItem[] {
    if (!currentUser) {
        return [];
    }
    return [
        {
            id: 'analytics',
            useRouterLink: true,
            link: AppUrls.ANALYTICS,
            iconClass: 'icn icn-dashboard',
            label: 'navigation.analytics',
            visible: true,
            children: [
                {
                    id: 'dashboard.tabs.overview',
                    link: AppUrls.BUSINESS_OVERVIEW,
                    iconClass: 'icn icn-dashboard',
                    label: 'dashboard.tabs.overview',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: currentUser.permissions.canViewPublishersDashboard,
                },
                {
                    id: 'dashboard.tabs.monetization',
                    link: AppUrls.MONETIZATION,
                    iconClass: 'icn icn-dashboard',
                    label: 'dashboard.tabs.monetization',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: currentUser.permissions.canViewPublishersDashboard,
                },
                {
                    id: 'dashboard.tabs.reports',
                    link: AppUrls.ANALYTICS_REPORTS,
                    iconClass: 'icn icn-dashboard',
                    label: 'dashboard.tabs.reports',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: currentUser.permissions.canViewPublishersDashboard || !currentUser.permissions.canViewOldDashboard,
                },
                {
                    id: 'dashboard.tabs.dashboard',
                    link: ['v3', AppUrls.DASHBOARD].join('/'),
                    iconClass: 'icn icn-dashboard',
                    label: 'dashboard.tabs.dashboard',
                    useRouterLink: true,
                    exactMatch: true,
                    visible: currentUser.permissions.canViewOldDashboard,
                },
                {
                    id: 'dashboard.tabs.internalDashboard',
                    link: AppUrls.DASHBOARD_INTERNAL,
                    iconClass: 'icn',
                    label: 'dashboard.tabs.internalDashboard',
                    useRouterLink: true,
                    visible: currentUser.permissions.canViewInternalDashboard,
                },
                {
                    id: 'dashboard.tabs.retentionRate',
                    link: AppUrls.DASHBOARD_RETENTION_RATE,
                    iconClass: 'icn',
                    label: 'dashboard.tabs.retentionRate',
                    visible: currentUser.permissions.canViewDashboardRetentionRate,
                },
                {
                    id: 'dashboard.tabs.ltv',
                    link: AppUrls.DASHBOARD_LTV,
                    iconClass: 'icn',
                    label: 'dashboard.tabs.ltv',
                    visible: currentUser.permissions.canViewDashboardLtv,
                },
            ],
        },
        {
            id: 'whitelabel/users',
            link: path(AppUrls.WHITE_LABEL_USERS, currentUser.wlDomainId),
            iconClass: 'icn icn-user',
            label: 'navigation.users',
            visible: currentUser.permissions.canViewWhiteLabel,
        },
        {
            id: 'whitelabel',
            link: AppUrls.WHITE_LABEL,
            iconClass: 'icn icn-api-integration',
            label: 'navigation.whitelabel',
            visible: currentUser.permissions.canViewWhiteLabel,
        },
        {
            id: 'apps',
            link: AppUrls.APPS,
            useRouterLink: false,
            iconClass: 'icn icn-apps',
            label: shortLabelMenu(currentUser, 'navigation.apps', navExpanded),
            visible: currentUser.permissions.canViewApps,
            children: [
                {
                    id: 'apps/appEdit',
                    link: AppUrls.APPS_UPDATE,
                    useRouterLink: true,
                    iconClass: AppPlatformIcons[`${currentAppInfo.platform}`],
                    label: currentAppInfo.name,
                    visible: Boolean(currentAppInfo.id),
                    children: [
                        {
                            id: 'apps/dcc/appsUpdate',
                            link: AppUrls.APPS_UPDATE,
                            iconClass: 'icn',
                            label: 'navigation.general',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: true,
                        },
                        {
                            id: 'apps/dcc/mediationGroups',
                            link: AppUrls.DCC_MEDIATION_GROUPS,
                            iconClass: 'icn',
                            label: 'navigation.mediationGroups',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: currentAppInfo.permissions?.canViewNewMediationSettings,
                            sup: 'dcc.mediationGroups.newSup',
                        },
                        {
                            id: 'apps/dcc/lineItems',
                            link: AppUrls.DCC_LINE_ITEMS,
                            iconClass: 'icn',
                            label: 'navigation.lineItems',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: true,
                        },
                        {
                            id: 'apps/dcc/configurations',
                            link: AppUrls.DCC_CONFIGURATIONS,
                            iconClass: 'icn',
                            label: 'navigation.adWaterfall',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: manageConfigurationPageInDcc(currentAppInfo),
                        },
                        {
                            id: 'v2/placement',
                            link: ['v3', AppUrls.APP_PLACEMENT].join('/'),
                            iconClass: 'icn',
                            label: 'navigation.placements',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: true,
                        },
                        {
                            id: 'apps/setup/attribution_settings',
                            link: AppUrls.APP_ATTRIBUTION_SETTINGS,
                            iconClass: 'icn',
                            label: 'navigation.attribution',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: currentUser.permissions.canViewApps,
                        },
                        {
                            id: 'apps/setup/events',
                            link: AppUrls.APP_EVENTS,
                            iconClass: 'icn',
                            label: 'navigation.funnels.events',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: currentUser.permissions.canViewFunnels,
                        },
                    ],
                },
                {
                    id: 'apps/appList',
                    link: AppUrls.APPS,
                    exactMatch: true,
                    useRouterLink: true,
                    iconClass: 'icn icn-apps',
                    label: shortLabelMenu(currentUser, Boolean(currentAppInfo.id) ? 'navigation.appList' : 'navigation.apps', navExpanded),
                    visible: currentUser.permissions.canViewApps,
                },
            ],
        },
        {
            id: 'mediationSetup',
            link: AppUrls.INTEGRATIONS_MEDIATION_AD_NETWORKS,
            iconClass: 'icn icn-mediation',
            label: 'navigation.mediation',
            visible: true,
            children: [
                {
                    id: 'integrations/mediationAdNetworks',
                    link: AppUrls.INTEGRATIONS_MEDIATION_AD_NETWORKS,
                    iconClass: 'icn',
                    label: 'navigation.adNetworks',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: true,
                },
                {
                    id: 'integrations/mediations',
                    link: AppUrls.MEDIATIONS,
                    iconClass: 'icn',
                    label: 'navigation.mediations',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: currentUser.permissions.canViewNewMediationSettings,
                },
                {
                    id: 'v2/segments',
                    link: ['v3', AppUrls.SEGMENTS].join('/'),
                    iconClass: 'icn icn-segments',
                    label: 'navigation.segments',
                    useRouterLink: true,
                    reloadRoute: true,
                    visible: currentUser.permissions.canViewSegments,
                },
                {
                    id: 'header.userMenu.appAdsTxt',
                    link: AppUrls.USER_PROFILE_ADS_TXT,
                    iconClass: 'icn',
                    label: 'navigation.appTxt',
                    visible: true,
                    useRouterLink: true,
                },
            ],
        },
        {
            id: 'abTesting',
            link: AppUrls.EXPERIMENTS_COLLECTION,
            iconClass: 'icn icn-experiments',
            label: 'navigation.abTesting',
            useRouterLink: true,
            visible: currentUser.permissions.canViewExperiments,
            children: [
                {
                    id: 'experiments/collection',
                    link: AppUrls.EXPERIMENTS_COLLECTION,
                    iconClass: 'icn icn-experiments',
                    label: 'navigation.experiments.list',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: true,
                },
                {
                    id: 'experiments/results',
                    link: '',
                    iconClass: 'icn icn-experiments',
                    label: 'navigation.experiments.results',
                    useRouterLink: true,
                    exactMatch: false,
                    visible: false,
                },
            ],
        },

        {
            id: 'integrations/userAcquisition',
            link: AppUrls.INTEGRATIONS_USER_ACQUISITION,
            iconClass: 'icn icn-user-acquisition',
            label: 'navigation.userAcquisition',
            useRouterLink: true,
            exactMatch: false,
            visible: true,
            children: [
                {
                    id: 'creativeLibraryLink',
                    link: AppUrls.CREATIVE_LIBRARY,
                    iconClass: 'icn icn-creativeLibrary',
                    label: 'navigation.creativeLibrary',
                    visible: document.location.pathname.startsWith('/' + AppUrls.CREATIVE_LIBRARY),
                },
                {
                    id: 'campaignManagement',
                    link: '/analytics/reports',
                    iconClass: 'icn',
                    label: 'navigation.campaignManagement',
                    useRouterLink: true,
                    visible: currentUser.permissions.canViewPublishersDashboard || !currentUser.permissions.canViewOldDashboard,
                },
                {
                    id: 'userAcquisition/setup',
                    link: AppUrls.INTEGRATIONS_USER_ACQUISITION,
                    useRouterLink: true,
                    iconClass: 'icn',
                    label: 'navigation.general',
                    visible: true,
                    exactMatch: false,
                    children: [
                        {
                            id: 'userAcquisition/setup/mmp_accounts',
                            link: AppUrls.INTEGRATIONS_USER_ACQUISITION,
                            iconClass: 'icn',
                            label: 'navigation.mmp',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: true,
                        },
                        {
                            id: 'userAcquisition/setup/network_accounts',
                            link: AppUrls.INTEGRATIONS_BID_MANAGER,
                            iconClass: 'icn',
                            label: 'navigation.network',
                            useRouterLink: true,
                            exactMatch: false,
                            visible: currentUser.permissions.bidManagerPermissions.canManageUaAccounts,
                        },

                        // creative Library links
                        {
                            id: 'creativeLibrary/uploads',
                            link: AppUrls.CREATIVE_LIBRARY_UPLOADS,
                            useRouterLink: true,
                            iconClass: 'icn icn-creativeLibrary',
                            label: 'creativeLibrary.uploads',
                            exactMatch: false,
                            queryParams: 'preserve',
                            visible: document.location.pathname.startsWith('/' + AppUrls.CREATIVE_LIBRARY),
                        },
                        {
                            id: 'creativeLibrary/creatives',
                            link: AppUrls.CREATIVE_LIBRARY_CREATIVES,
                            useRouterLink: true,
                            iconClass: 'icn icn-creativeLibrary',
                            label: 'creativeLibrary.creatives',
                            exactMatch: false,
                            queryParams: 'preserve',
                            visible: document.location.pathname.startsWith('/' + AppUrls.CREATIVE_LIBRARY),
                        },
                        {
                            id: 'creativeLibrary/testResults',
                            link: AppUrls.CREATIVE_LIBRARY_TEST_RESULTS,
                            useRouterLink: true,
                            iconClass: 'icn icn-creativeLibrary',
                            label: 'creativeLibrary.testResults',
                            exactMatch: false,
                            queryParams: 'preserve',
                            visible: document.location.pathname.startsWith('/' + AppUrls.CREATIVE_LIBRARY),
                        },
                        {
                            id: 'creativeLibrary/testFlow',
                            link: AppUrls.CREATIVE_LIBRARY_TEST_FLOW,
                            useRouterLink: true,
                            iconClass: 'icn icn-creativeLibrary',
                            label: 'creativeLibrary.testFlow',
                            exactMatch: false,
                            queryParams: 'preserve',
                            visible: document.location.pathname.startsWith('/' + AppUrls.CREATIVE_LIBRARY),
                        },
                    ],
                },
            ],
        },
        {
            id: 'bidon',
            link: AppUrls.BIDON,
            iconClass: 'icn icn-bidon',
            label: 'navigation.bidon',
            secure: true,
            visible: currentUser.permissions.canViewEmbeddedBidonUi,
        },
        {
            id: 'bidManager',
            link: AppUrls.BID_MANAGER,
            iconClass: 'icn icn-bidmanager',
            label: 'bidManager.pageTitle',
            visible:
                currentUser.permissions.bidManagerPermissions.canViewBidmanager &&
                (currentUser.permissions.bidManagerPermissions.canManageCreatives ||
                    currentUser.permissions.bidManagerPermissions.canManageCampaigns),
            children: [
                {
                    id: 'bidManager.creatives',
                    link: AppUrls.BID_MANAGER_CREATIVES,
                    useRouterLink: true,
                    iconClass: 'icn icn-bidmanager',
                    label: 'bidManager.creativesPage',
                    visible: currentUser.permissions.bidManagerPermissions.canManageCreatives,
                    exactMatch: false,
                },
                {
                    id: 'bidManager.campaigns',
                    link: AppUrls.BID_MANAGER_CAMPAIGNS,
                    useRouterLink: true,
                    iconClass: 'icn icn-bidmanager',
                    label: 'bidManager.tabs.campaigns',
                    visible: currentUser.permissions.bidManagerPermissions.canManageCampaigns,
                    exactMatch: false,
                },
                {
                    id: 'bidManager.accounts',
                    link: AppUrls.INTEGRATIONS_BID_MANAGER,
                    useRouterLink: true,
                    iconClass: 'icn icn-bidmanager',
                    label: 'bidManager.tabs.accounts',
                    visible: currentUser.permissions.bidManagerPermissions.canManageUaAccounts,
                    exactMatch: false,
                },
            ],
        },

        {
            id: 'funnels',
            link: AppUrls.ANALYTICS_FUNNELS,
            iconClass: 'icn icn-funnels',
            label: 'navigation.funnels.collection',
            useRouterLink: true,
            visible: currentUser.permissions.canViewFunnels,
            sup: 'beta',
            children: [
                {
                    id: 'funnels/collection',
                    link: AppUrls.ANALYTICS_FUNNELS,
                    iconClass: 'icn icn-funnels',
                    label: 'navigation.funnels.collection',
                    useRouterLink: true,
                    visible: currentUser.permissions.canViewFunnels,
                },
                {
                    id: 'funnels/events',
                    link: AppUrls.APP_EVENTS,
                    iconClass: 'icn icn-funnels',
                    label: 'navigation.funnels.events',
                    useRouterLink: true,
                    visible: currentUser.permissions.canViewFunnels,
                },
            ],
        },
        {
            id: 'adServer',
            link: ['v3', AppUrls.AD_SERVER].join('/'),
            iconClass: 'icn icn-campaigns',
            label: 'navigation.ad_server',
            useRouterLink: true,
            reloadRoute: true,
            visible: currentUser.permissions.canViewAdServer,
        },
        {
            id: 'billing',
            link: AppUrls.PAYOUTS_REPORT,
            useRouterLink: true,
            iconClass: 'icn icn-billing',
            label: 'navigation.billing',
            visible: currentUser.permissions.canViewPayouts,
            children: [
                {
                    id: 'payouts.tabs.reports',
                    link: AppUrls.PAYOUTS_REPORT,
                    useRouterLink: true,
                    iconClass: 'icn icn-billing',
                    label: 'payouts.tabs.reports',
                    visible: true,
                },
                {
                    id: 'payouts.tabs.requests',
                    link: AppUrls.PAYOUTS_REQUEST,
                    useRouterLink: true,
                    iconClass: 'icn icn-billing',
                    label: 'payouts.tabs.requests',
                    visible: true,
                },
                {
                    id: 'payouts.tabs.details',
                    link: AppUrls.PAYOUTS_DETAILS,
                    useRouterLink: true,
                    iconClass: 'icn icn-billing',
                    label: 'payouts.tabs.details',
                    visible: true,
                },
                {
                    id: 'payouts.tabs.publishing',
                    link: AppUrls.PAYOUTS_PUBLISHING,
                    useRouterLink: true,
                    iconClass: 'icn icn-billing',
                    label: 'payouts.tabs.publishing',
                    visible: document.location.pathname.startsWith('/' + AppUrls.PAYOUTS_PUBLISHING),
                },
                {
                    id: 'payouts.tabs.ambassadorProgram',
                    link: ['v3', AppUrls.PAYOUTS_AMBASSADOR_PROGRAM].join('/'),
                    useRouterLink: true,
                    iconClass: 'icn icn-billing',
                    label: 'payouts.tabs.ambassadorProgram',
                    visible: currentUser.permissions.canViewAmbassadorProgram,
                },
            ],
        },
        {
            id: 'documentation',
            link: path(AppUrls.DOCS, language),
            target: '_blank',
            iconClass: 'icn icn-docs',
            label: shortLabelMenu(currentUser, 'navigation.docs', navExpanded),
            visible: currentUser.permissions.canViewSdkDocs,
            trackEvent: 'Docs clicked',
        },
        {
            id: 'support',
            link: faqURL || AppUrls.FAQ,
            iconClass: 'icn icn-support',
            label: 'navigation.support',
            visible: true,
            children: [
                {
                    id: 'contact_us',
                    link: 'https://mail.google.com/mail/?view=cm&fs=1&to=support@appodeal.com',
                    target: '_blank',
                    iconClass: 'icn icn-contact-us',
                    label: 'navigation.contactUs',
                    visible: true,
                    openIntercom: false,
                    trackEvent: 'Contact us clicked',
                },
                {
                    id: 'faq',
                    // url can be custom
                    link: faqURL || AppUrls.FAQ,
                    iconClass: 'icn icn-faq',
                    label: 'navigation.faq',
                    target: '_blank',
                    visible: currentUser.permissions.canViewFaq,
                    trackEvent: 'FAQ clicked',
                },
                {
                    id: 'integration',
                    link: path(AppUrls.API_INTEGRATION, language, language === 'ru' ? 'api-dokumentatsiya' : 'reporting-api'),
                    target: '_blank',
                    iconClass: 'icn icn-api-integration',
                    label: 'navigation.api',
                    visible: currentUser.permissions.canViewIntegrations,
                    trackEvent: 'API documentation clicked',
                },
                {
                    id: 'homepage',
                    link: path(AppUrls.APPODEAL_HOMEPAGE, language),
                    target: '_blank',
                    iconClass: 'icn',
                    label: 'navigation.appodealHomepage',
                    visible: true,
                },
                {
                    id: 'appodealBlog',
                    link: path(AppUrls.APPODEAL_BLOG, language),
                    target: '_blank',
                    iconClass: 'icn',
                    label: 'navigation.appodealBlog',
                    visible: true,
                },
            ],
        },
        {
            id: 'myAccounts',
            link: AppUrls.PROFILE_EDIT,
            iconClass: 'icn icn-my-account',
            label: 'navigation.account',
            useRouterLink: true,
            visible: true,
            children: [
                {
                    id: 'settings',
                    link: AppUrls.USER_PROFILE,
                    iconClass: 'icn',
                    label: 'navigation.settings.main',
                    visible: true,
                    useRouterLink: true,
                    children: [
                        {
                            id: 'account',
                            link: AppUrls.USER_PROFILE_EDIT_ACCOUNT,
                            iconClass: 'icn',
                            label: 'navigation.settings.account',
                            visible: true,
                            useRouterLink: true,
                        },
                        {
                            id: 'security',
                            link: AppUrls.USER_PROFILE_EDIT_SECURITY,
                            iconClass: 'icn',
                            label: 'navigation.settings.security',
                            visible: true,
                            useRouterLink: true,
                        },
                        {
                            id: 'apiCredentials',
                            link: AppUrls.USER_PROFILE_CREDENTIALS,
                            iconClass: 'icn',
                            label: 'navigation.settings.api',
                            visible: true,
                            useRouterLink: true,
                        },
                    ],
                },
                {
                    id: 'accessSharing',
                    link: AppUrls.APPS_SHARING,
                    iconClass: 'icn',
                    label: 'navigation.accessSharing',
                    visible: true,
                    useRouterLink: true,
                    reloadRoute: true,
                },
            ],
        },

        {
            id: 'admin',
            link: AppUrls.PANEL,
            iconClass: 'icn icn-panel',
            label: 'navigation.panel',
            secure: true,
            visible: currentUser.permissions.canViewAdminPanel,
        },
    ];
}

export function shortLabelMenu(currentUser: CurrentUser, label: string, navExpanded: boolean): string {
    if (currentUser?.locale?.toLowerCase() !== 'ru') {
        return label;
    }

    if (!navExpanded) {
        let newLabel = label.split('.');
        newLabel.splice(newLabel.length - 1, 0, 'short');
        return newLabel.join('.');
    }

    return label;
}
